// Formatters + curve-merging utilities shared across the PayoffChart family.
// Kept dependency-free so they're cheap to import from sub-components.

import React from "react";

export const fmt$ = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

export const fmtPnL = (n) => {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}$${Math.abs(n) >= 100 ? Math.round(n).toLocaleString() : Number(n).toFixed(2)}`;
};

export const fmtPnLPct = (n) => {
  if (n == null || !isFinite(n)) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

// OptionStrat-style tooltip: shows price, % move from spot, P/L at expiry,
// and the T+0 (today) P/L so users can see how time decay affects them.
// `mode` ('dollars'|'percent') flips P&L units; `riskBasis` is the denominator.
export const buildTooltip = (spot, mode, riskBasis) => ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const positive = d.pnl >= 0;
  const todayPositive = d.today >= 0;
  const pctMove = spot ? ((d.price - spot) / spot) * 100 : null;
  const fmtVal = (v) => {
    if (v == null) return "—";
    if (mode === "percent" && riskBasis > 0) return fmtPnLPct((v / riskBasis) * 100);
    return fmtPnL(v);
  };
  return (
    <div className="bg-[#0a0a0c] border border-white/15 rounded-md px-3 py-2 text-[11px] shadow-xl min-w-[170px]">
      <div className="num text-white flex items-baseline justify-between gap-2">
        <span>{fmt$(d.price)}</span>
        {pctMove != null && (
          <span
            className={`text-[10px] num ${pctMove >= 0 ? "text-[#00C805]/80" : "text-[#FF3B30]/80"}`}
          >
            {pctMove >= 0 ? "+" : ""}{pctMove.toFixed(2)}%
          </span>
        )}
      </div>
      <div className="border-t border-white/10 mt-1.5 pt-1.5 space-y-0.5">
        <div className="flex justify-between gap-3">
          <span className="text-white/55 uppercase tracking-[0.16em] text-[9px]">At expiry</span>
          <span className={`num font-medium ${positive ? "text-[#00C805]" : "text-[#FF3B30]"}`}>
            {fmtVal(d.pnl)}
          </span>
        </div>
        {d.today != null && (
          <div className="flex justify-between gap-3">
            <span className="text-white/55 uppercase tracking-[0.16em] text-[9px]">T+0 today</span>
            <span className={`num ${todayPositive ? "text-[#00C805]/80" : "text-[#FF3B30]/80"}`}>
              {fmtVal(d.today)}
            </span>
          </div>
        )}
        {d.probPdf != null && d.probPdf > 0 && (
          <div className="flex justify-between gap-3 pt-0.5 border-t border-white/5 mt-0.5">
            <span className="text-[#A98CFF]/70 uppercase tracking-[0.16em] text-[9px]">Likelihood</span>
            <span className="num text-[#A98CFF]/85 text-[10px]">
              {(d.probPdf * 100).toFixed(2)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Merge the expiry curve + today curve (same x grid, matched on price) into
// a single series so the chart can render two lines + split fills. Also
// computes the lognormal probability density at each price (powers the
// "bell curve" overlay) and applies an optional IV-shock vega adjustment
// to the today curve.
export function mergeCurves(expiry, today, volumeByStrike, cone, spot, ivShockVp, vegaTotal) {
  if (!expiry?.length) return [];
  const byPrice = new Map();
  (today || []).forEach((pt) => byPrice.set(pt.price, pt.pnl));
  // Volume bucket: snap to nearest grid price.
  const strikes = volumeByStrike ? Object.keys(volumeByStrike).map(Number) : [];
  const findVol = (price) => {
    if (!strikes.length) return 0;
    const k = strikes.reduce((a, b) => (Math.abs(b - price) < Math.abs(a - price) ? b : a));
    return Math.abs(k - price) <= 5 ? (volumeByStrike[k] || 0) : 0;
  };
  // Lognormal sigma for the cone (matches backend: sigma_hi = spot*exp(σ_t)).
  let sigmaT = null;
  if (cone?.sigma_hi != null && spot && cone.sigma_hi > 0) {
    sigmaT = Math.log(cone.sigma_hi / spot);
    if (!isFinite(sigmaT) || sigmaT <= 0) sigmaT = null;
  }
  const ivShockDelta = (vegaTotal && ivShockVp) ? vegaTotal * ivShockVp : 0;
  // First pass: compute raw values, track pdf max for normalization.
  let pdfMax = 0;
  const raw = expiry.map((pt) => {
    const todayRaw = byPrice.get(pt.price);
    const todayAdj = todayRaw != null ? todayRaw + ivShockDelta : null;
    let pdf = 0;
    if (sigmaT && pt.price > 0 && spot) {
      const x = Math.log(pt.price / spot);
      // Normal in log-space, drift-free for visual cone consistency.
      pdf = Math.exp(-(x * x) / (2 * sigmaT * sigmaT));
      if (pdf > pdfMax) pdfMax = pdf;
    }
    return {
      price: pt.price,
      pnl: pt.pnl,
      profit: pt.pnl >= 0 ? pt.pnl : 0,
      loss: pt.pnl < 0 ? pt.pnl : 0,
      today: todayAdj,
      volume: findVol(pt.price),
      _pdfRaw: pdf,
    };
  });
  // Normalize pdf → 0..1, expose probPdf (relative likelihood).
  return raw.map((r) => ({
    ...r,
    prob: pdfMax > 0 ? r._pdfRaw / pdfMax : 0,
    probPdf: pdfMax > 0 ? r._pdfRaw / pdfMax : 0,
  }));
}

// Snap helper — when listedStrikes is provided, returns the closest one.
export const snapPrice = (raw, listedStrikes) => {
  if (!listedStrikes || !listedStrikes.length) {
    // Default: round to nearest $0.50 (typical listed-strike granularity)
    return Math.round(raw * 2) / 2;
  }
  let best = listedStrikes[0];
  let bestDist = Math.abs(best - raw);
  for (let i = 1; i < listedStrikes.length; i++) {
    const d = Math.abs(listedStrikes[i] - raw);
    if (d < bestDist) {
      bestDist = d;
      best = listedStrikes[i];
    }
  }
  return best;
};

// Recharts' default plot area is inset by YAxis (width=70 left) and margin
// right=18, so the data region maps roughly to [70, totalWidth-18].
export const PLOT_PAD_LEFT = 70;
export const PLOT_PAD_RIGHT = 18;
